import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, MagnifyingGlass, Link as LinkIcon, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Phase 2D — Admin tool to diagnose "Email already exists" conflicts and to
// attach orphan auto-created client accounts to the admin's current tenant.
// Reuses backend endpoints: GET /admin/users/by-email + POST /admin/users/{id}/attach-current-company.
// No token is ever displayed. No password_hash is ever fetched (backend strips it).
// No write happens without explicit confirmation.

const Row = ({ label, value, testid }) => (
  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 sm:gap-4 py-2 border-b border-zinc-800/60 last:border-0">
    <span className="text-xs uppercase tracking-wider text-zinc-500">{label}</span>
    <span data-testid={testid} className="text-sm text-zinc-200 font-mono break-all sm:text-right">
      {value === undefined || value === null || value === "" ? <em className="text-zinc-600 not-italic">—</em> : String(value)}
    </span>
  </div>
);

export default function AdminClientDiagnostic() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState(null);   // diagnostic payload or null
  const [error, setError] = useState("");

  const runDiagnostic = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const v = email.trim().toLowerCase();
    if (!v) { toast.error("Renseignez un email"); return; }
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.get(`/admin/users/by-email`, { params: { email: v } });
      setResult(data);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 404) {
        setError("Aucun compte ne correspond à cet email.");
      } else {
        setError(typeof detail === "string" ? detail : "Échec du diagnostic.");
      }
    } finally { setLoading(false); }
  };

  const doAttach = async () => {
    if (!result?.id) return;
    setAttaching(true);
    try {
      const { data } = await api.post(`/admin/users/${result.id}/attach-current-company`);
      toast.success(`Compte rattaché au tenant « ${data.company_id} »`);
      // Auto-rerun diagnostic so the screen reflects the new state.
      await runDiagnostic();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Échec du rattachement");
    } finally {
      setAttaching(false);
      setConfirmOpen(false);
    }
  };

  const canAttach = Boolean(result?.can_attach_to_current_company);

  return (
    <div data-testid="admin-client-diagnostic" className="min-h-screen bg-[#050505]">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#050505]/80 border-b border-zinc-800/50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          <Button data-testid="diag-back-btn" variant="ghost" size="sm" onClick={() => navigate("/admin")} className="text-zinc-400 hover:text-white hover:bg-zinc-800">
            <ArrowLeft size={18} /><span className="ml-1.5 hidden sm:inline">Admin</span>
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-semibold text-white truncate">Diagnostic client par email</h1>
            <p className="text-[11px] text-zinc-500 truncate">Identifier et rattacher un compte client orphelin</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* Search form */}
        <form onSubmit={runDiagnostic} className="bg-[#0F0F0F] border border-zinc-800 rounded-xl p-5 space-y-3">
          <div className="space-y-1">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Email du client</Label>
            <div className="flex gap-2">
              <Input
                data-testid="diag-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ex. client@example.com"
                autoComplete="off"
                className="flex-1 bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10"
              />
              <Button
                data-testid="diag-submit-btn"
                type="submit"
                disabled={loading}
                className="bg-amber-500 text-black font-semibold hover:bg-amber-600 h-10 px-4"
              >
                <MagnifyingGlass size={16} weight="bold" className="mr-1.5" />
                {loading ? "…" : "Diagnostiquer"}
              </Button>
            </div>
          </div>
          <p className="text-[11px] text-zinc-600">
            Lecture seule. Aucun mot de passe ni hash n'est jamais affiché.
          </p>
        </form>

        {/* Error state */}
        {error && (
          <div data-testid="diag-error" className="bg-red-900/20 border border-red-900/40 rounded-xl p-4 flex items-start gap-3">
            <Warning size={20} className="text-red-400 shrink-0 mt-0.5" />
            <p className="text-sm text-red-300">{error}</p>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="bg-[#0F0F0F] border border-zinc-800 rounded-xl p-5 space-y-1">
            <div className="flex items-center justify-between gap-3 pb-3 mb-3 border-b border-zinc-800">
              <h2 className="text-sm font-semibold text-white">Compte trouvé</h2>
              {canAttach ? (
                <span className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 px-2 py-1 rounded-full uppercase tracking-wider">
                  Rattachement possible
                </span>
              ) : (
                <span className="text-[11px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 px-2 py-1 rounded-full uppercase tracking-wider flex items-center gap-1">
                  <CheckCircle size={12} weight="fill" /> Conforme
                </span>
              )}
            </div>

            <Row label="ID"                        value={result.id}                            testid="diag-field-id" />
            <Row label="Email"                     value={result.email}                         testid="diag-field-email" />
            <Row label="Nom"                       value={result.name}                          testid="diag-field-name" />
            <Row label="Téléphone"                 value={result.phone}                         testid="diag-field-phone" />
            <Row label="Rôle"                      value={result.role}                          testid="diag-field-role" />
            <Row label="Type de compte"            value={result.account_type}                  testid="diag-field-account-type" />
            <Row label="Statut"                    value={result.account_status}                testid="diag-field-account-status" />
            <Row label="Tenant (company_id)"       value={result.company_id}                    testid="diag-field-company-id" />
            <Row label="Demande d'origine"         value={result.auto_created_from_request_id}  testid="diag-field-origin-rr" />
            <Row label="Créé le"                   value={result.created_at}                    testid="diag-field-created-at" />
            <Row label="Éligible au rattachement"  value={canAttach ? "Oui" : "Non"}            testid="diag-field-can-attach" />

            {canAttach && (
              <div className="pt-4 mt-2 border-t border-zinc-800">
                <Button
                  data-testid="diag-attach-btn"
                  onClick={() => setConfirmOpen(true)}
                  disabled={attaching}
                  className="w-full sm:w-auto bg-amber-500 text-black font-semibold hover:bg-amber-600 h-10 px-4"
                >
                  <LinkIcon size={16} weight="bold" className="mr-1.5" />
                  Rattacher au tenant
                </Button>
                <p className="text-[11px] text-zinc-600 mt-2">
                  Seul le champ <code className="text-zinc-400">company_id</code> sera modifié. Email, mot de passe, rôle et type de compte restent inchangés.
                </p>
              </div>
            )}
          </div>
        )}
      </main>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="diag-confirm-dialog" className="bg-[#0F0F0F] border-zinc-800 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer le rattachement</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Vous allez rattacher le compte <span className="text-amber-300 font-mono">{result?.email}</span> au tenant courant.
              Cette action met à jour uniquement le champ <code className="text-zinc-300">company_id</code>.
              Aucun autre champ n'est touché. Cette action est nominalement irréversible depuis cet écran.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="diag-confirm-cancel" className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="diag-confirm-ok"
              onClick={doAttach}
              disabled={attaching}
              className="bg-amber-500 text-black hover:bg-amber-600"
            >
              {attaching ? "Rattachement…" : "Confirmer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
